import { EventName } from './events'

const IMPRESSION_SAMPLE_RATE = 0.1

// Impression events that bypass sampling: always sent at 100% and NOT flagged
// `sampled`, so admin queries must count them at 1x (never x10 — see popups.ts).
// Mirrors the old app's `event !== 'gsc-show'` carve-out in
// ../next/src/lib/tracking.ts — gsc popup shows are low-volume, so we keep exact
// counts rather than a 10%-scaled estimate.
const SAMPLING_EXEMPT_EVENTS = new Set<string>([EventName.GscShow])

// The DB-driven site popups (popup-<slug>--impression) started out exempt like
// gsc-show, but sitewide campaigns put impressions at pageview scale, so they
// now take the standard 10% sample to PostHog. site_events still records every
// impression (the beacon writes allEvents regardless); the /admin/analytics
// popups report scales sampled rows ×10 (see queries/popups.ts). Clicks and
// dismisses are never sampled.

export const isSamplingExempt = (eventName: string): boolean => SAMPLING_EXEMPT_EVENTS.has(eventName)

// Per-impression coin flip, matching the old app. This must stay a fresh
// random draw per impression: a deterministic per-visitor hash
// (hash(distinctId:eventName) < 0.1) was tried and reverted — it samples
// PEOPLE, not impressions, so the same ~10% of visitors are in the bucket
// forever and the realized daily rate drifted between 5.8% and 12.1%
// (measured against site_events) while dashboards scale by a fixed ×10.
// That error is persistent — it never averages out across days.
// Context: docs/decisions/2026-07-13-display-ad-impression-reporting.md
export const shouldSampleImpression = (eventName: string): boolean => {
  if (isSamplingExempt(eventName)) {
    return true
  }
  return Math.random() < IMPRESSION_SAMPLE_RATE
}

export const getImpressionSampleRate = (): number => IMPRESSION_SAMPLE_RATE

// $server-pageview → PostHog is sampled at 10% (fresh coin flip per event, same
// reasoning as impressions above). site_events gets EVERY server pageview — it
// stays the exact source of truth; the PostHog copy exists only for
// cross-checks and bot/device-share estimates, so its consumers scale ×10
// (see src/lib/monitor/summary/posthog-metrics.ts). At full rate this was the
// site's single biggest billable event (~2.3M/month).
const SERVER_PAGEVIEW_SAMPLE_RATE = 0.1

export const shouldSampleServerPageview = (): boolean => Math.random() < SERVER_PAGEVIEW_SAMPLE_RATE

export const getServerPageviewSampleRate = (): number => SERVER_PAGEVIEW_SAMPLE_RATE
